import { useQueries, useQuery } from '@tanstack/react-query'
import {
  ArrowRight,
  BookOpen,
  Calendar,
  Check,
  CheckCircle2,
  Coins,
  Compass,
  Copy,
  Globe,
  HelpCircle,
  Lightbulb,
  Megaphone,
  Radio,
  RefreshCw,
  Snowflake,
  Sparkles,
  Tag,
  Target,
  TrendingUp,
  Users,
  X,
} from 'lucide-react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { useApp } from '../context/AppContext'
import { DataFlowHero } from '../components/DataFlowHero'
import {
  type DynamicBriefing,
  INITIAL_BRIEFING,
  PRIORITY_OPTIONS,
  QUICK_ACTIONS,
  type StudioStage,
} from '../data/campaignStudioData'
import { Badge, Button } from '../components/ui'
import { askWhat, askWhen, askWho, getAnalyticsInterest } from '../lib/api'
import { type DataProvenance, deriveStudio } from '../lib/studioEngine'
import { interpretPrompt } from '../lib/studioInterpreter'
import type { Answer } from '../lib/types'
import { cn, pick } from '../lib/utils'

const PROVENANCE_STYLE: Record<DataProvenance, string> = {
  real: 'border-emerald-500/40 text-emerald-400 bg-emerald-500/10',
  simulated: 'border-rose-500/40 text-rose-400 bg-rose-500/10',
  mixed: 'border-amber-500/40 text-amber-400 bg-amber-500/10',
}

/** Sello que dice si un bloque sale de datos reales, simulados o mixtos. */
function ProvenanceBadge({ kind, label }: { kind: DataProvenance; label: string }) {
  return (
    <span
      className={cn(
        'inline-flex items-center rounded border px-1.5 py-0 text-[9px] font-semibold uppercase tracking-wide',
        PROVENANCE_STYLE[kind],
      )}
      title={label}
    >
      {label}
    </span>
  )
}

export function CampaignStudio() {
  const { t } = useTranslation()
  const { locale, audience } = useApp()

  // Datos reales del motor + inteligencia de demanda de GA.
  const results = useQueries({
    queries: [
      { queryKey: ['when', locale, audience], queryFn: () => askWhen({ locale, audience, horizon_months: 12 }), staleTime: 300_000 },
      { queryKey: ['who', locale, audience], queryFn: () => askWho({ locale, audience }), staleTime: 300_000 },
      { queryKey: ['what', locale, audience], queryFn: () => askWhat({ locale, audience, limit: 50 }), staleTime: 300_000 },
    ],
  })
  const interest = useQuery({
    queryKey: ['analytics-interest', locale],
    queryFn: () => getAnalyticsInterest(locale, 30),
    staleTime: 5 * 60_000,
  })

  const whenAns = results[0]?.data as Answer | undefined
  const whoAns = results[1]?.data as Answer | undefined
  const whatAns = results[2]?.data as Answer | undefined

  const [stage, setStage] = useState<StudioStage>('idle')
  const [inputText, setInputText] = useState('')
  const [selectedPriority, setSelectedPriority] = useState<string | null>(null)
  const [selectedPathway, setSelectedPathway] = useState<'product' | 'market' | 'season' | 'target' | null>(null)
  const [activeStepIndex, setActiveStepIndex] = useState(0)
  const [whyModalOpen, setWhyModalOpen] = useState(false)
  const [activeContentId, setActiveContentId] = useState<string>('instagram_ad')
  const [copiedNotification, setCopiedNotification] = useState(false)

  const liveInterpreted = useMemo(() => interpretPrompt(inputText), [inputText])
  const hasLiveDetected = Boolean(
    liveInterpreted.product ||
    liveInterpreted.market ||
    liveInterpreted.season ||
    liveInterpreted.targetAudience,
  )

  const PATHWAY_PRESETS = [
    {
      id: 'product' as const,
      icon: 'Tag',
      labelKey: 'studio.interpreter.pathwayProduct',
      subKey: 'studio.interpreter.pathwayProductSub',
      prompt: {
        es: 'Tengo safaris con huskies en Levi: ¿a qué mercados y en qué meses me conviene lanzar campaña?',
        en: 'I operate husky safaris in Levi: which source markets and months are best to launch my campaign?',
        fi: 'Tarjoan koiravaljakkoretkiä Levillä: mille markkinoille ja minä kuukausina minun kannattaa suunnata kampanja?',
      },
    },
    {
      id: 'market' as const,
      icon: 'Globe',
      labelKey: 'studio.interpreter.pathwayMarket',
      subKey: 'studio.interpreter.pathwayMarketSub',
      prompt: {
        es: 'Quiero atraer turistas alemanes durante la temporada baja de primavera y otoño con mis alojamientos.',
        en: 'I want to attract German travelers during the off-season (spring and autumn) with my accommodations.',
        fi: 'Haluan houkutella saksalaisia matkailijoita kevään ja syksyn hiljaisina viikkoina majoituksellani.',
      },
    },
    {
      id: 'season' as const,
      icon: 'Calendar',
      labelKey: 'studio.interpreter.pathwaySeason',
      subKey: 'studio.interpreter.pathwaySeasonSub',
      prompt: {
        es: 'Quiero maximizar la ocupación de mis cabañas y saunas en septiembre durante el otoño y la Ruska.',
        en: 'I want to maximize occupancy for my cabins and saunas in September during autumn colors and Ruska.',
        fi: 'Haluan maksimoida mökkieni ja saunojeni käyttöasteen syyskuussa ruskan ja ensirevontulien aikaan.',
      },
    },
    {
      id: 'target' as const,
      icon: 'Users',
      labelKey: 'studio.interpreter.pathwayTarget',
      subKey: 'studio.interpreter.pathwayTargetSub',
      prompt: {
        es: 'Quiero paquetizar actividades de nieve y alojamiento para familias británicas en vacaciones escolares.',
        en: 'I want to package snow activities and accommodation for British families during school breaks.',
        fi: 'Haluan paketoida talviaktiviteetteja ja majoitusta brittiläisille lapsiperheille talvilomakaudeksi.',
      },
    },
  ]

  const derived = useMemo(
    () =>
      deriveStudio({
        whenAns,
        whoAns,
        whatAns,
        interest: interest.data,
        locale,
        priorityId: selectedPriority,
        query: inputText,
      }),
    [whenAns, whoAns, whatAns, interest.data, locale, selectedPriority, inputText],
  )

  const briefing: DynamicBriefing = stage === 'idle' ? INITIAL_BRIEFING : derived.briefing
  const opportunity = derived.opportunity
  const campaign = derived.campaign
  const provenanceLabel: Record<DataProvenance, string> = {
    real: t('studio.provReal', 'Datos reales'),
    simulated: t('studio.provSimulated', 'Simulado'),
    mixed: t('studio.provMixed', 'Real + simulado'),
  }
  // Los niveles del motor vienen en mayusculas y en castellano; se traducen.
  const LEVEL_KEY: Record<string, string> = {
    ALTO: 'levelHigh', ALTA: 'levelHigh', MEDIO: 'levelMedium', MEDIA: 'levelMedium', BAJA: 'levelLow',
  }
  const levelLabel = (v: string) => t(`studio.${LEVEL_KEY[v] ?? 'levelMedium'}`)

  const analysisSteps = [
    { label: t('studio.steps.offer') },
    { label: t('studio.steps.markets') },
    { label: t('studio.steps.seasonality') },
    { label: t('studio.steps.profile') },
    { label: t('studio.steps.prioritizing') },
    { label: t('studio.steps.generation') },
  ]

  const renderIcon = (name: string, className = 'size-4 shrink-0') => {
    switch (name) {
      case 'Target':
        return <Target className={className} />
      case 'Calendar':
        return <Calendar className={className} />
      case 'Globe':
        return <Globe className={className} />
      case 'TrendingUp':
        return <TrendingUp className={className} />
      case 'Sparkles':
        return <Sparkles className={className} />
      case 'Coins':
        return <Coins className={className} />
      case 'Users':
        return <Users className={className} />
      case 'Tag':
        return <Tag className={className} />
      case 'Snowflake':
        return <Snowflake className={className} />
      case 'Radio':
        return <Radio className={className} />
      case 'Megaphone':
        return <Megaphone className={className} />
      default:
        return <Sparkles className={className} />
    }
  }

  const handleSelectPathway = (p: (typeof PATHWAY_PRESETS)[number]) => {
    setSelectedPathway(p.id)
    setInputText(pick(p.prompt, locale))
  }

  // Inicio del análisis cuando el usuario pulsa enviar o un chip rápido
  const handleStartAnalysis = (promptValue?: string) => {
    const text = promptValue || inputText
    if (!text.trim()) return

    setInputText(text)
    setStage('analyzing')
    setActiveStepIndex(0)

    // Simulación del progreso de análisis paso a paso
    const timer1 = setTimeout(() => setActiveStepIndex(1), 350)
    const timer2 = setTimeout(() => setActiveStepIndex(2), 700)
    const timer3 = setTimeout(() => setActiveStepIndex(3), 1050)
    const timer4 = setTimeout(() => {
      setActiveStepIndex(4)
      setStage('priority_prompt')
    }, 1500)

    return () => {
      clearTimeout(timer1)
      clearTimeout(timer2)
      clearTimeout(timer3)
      clearTimeout(timer4)
    }
  }

  // Selección de prioridad (ej. Temporada baja)
  const handleSelectPriority = (priorityId: string) => {
    setSelectedPriority(priorityId)
    setStage('opportunity_ready')
  }

  // Generación de propuesta de campaña
  const handleGenerateCampaign = () => {
    setStage('generating_campaign')
    setActiveStepIndex(5)
    setTimeout(() => {
      setStage('campaign_ready')
    }, 1400)
  }

  // Reiniciar escenario y volver a iniciar sin recargar la página
  const handleReset = () => {
    setStage('idle')
    setInputText('')
    setSelectedPriority(null)
    setSelectedPathway(null)
    setActiveStepIndex(0)
    setWhyModalOpen(false)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const handleCopyBriefing = () => {
    navigator.clipboard?.writeText(
      `NADIA CAMPAIGN BRIEFING:\n${t('studio.objectiveLabel')}: ${pick(campaign.objective, locale)}\n${t('studio.audienceLabel')}: ${pick(campaign.audience, locale)}\n${t('studio.timingLabel')}: ${campaign.period}\n${t('studio.creativeConceptLabel')}: ${campaign.creativeConcept}`,
    )
    setCopiedNotification(true)
    setTimeout(() => setCopiedNotification(false), 2500)
  }

  const activeContent =
    campaign.contents.find((c) => c.id === activeContentId) || campaign.contents[0]

  return (
    <div className="space-y-6 pb-12">
      {/* 2. Cabecera */}
      <div className="flex flex-col gap-2 border-b border-border/60 pb-5">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="space-y-1">
            <div className="flex items-center gap-2.5">
              <div className="flex size-7 items-center justify-center rounded-lg bg-primary/20 text-primary">
                <Sparkles className="size-4" />
              </div>
              <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight">
                {t('studio.title')}
              </h1>
              <Badge variant="outline" className="border-cyan-500/30 text-cyan-400 bg-cyan-500/10 text-[11px]">
                {t('studio.role')}
              </Badge>
            </div>
            <p className="text-sm sm:text-base text-muted-foreground font-normal">
              {t('studio.subtitle')}
            </p>
          </div>

          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground font-mono bg-muted/50 px-2.5 py-1 rounded-md border border-border/50">
              {t('studio.pilot')}
            </span>
            {stage !== 'idle' ? (
              <Button
                variant="outline"
                size="sm"
                onClick={handleReset}
                className="text-xs h-8 gap-1.5 font-bold cursor-pointer hover:bg-primary/10 hover:text-primary transition-all"
                title={t('studio.interpreter.startOverBtn', 'Reiniciar proceso')}
              >
                <RefreshCw className="size-3.5" />
                <span>{t('studio.interpreter.startOverBtn', 'Reiniciar proceso')}</span>
              </Button>
            ) : null}
          </div>
        </div>
      </div>

      {/* Hero: flujo del dato, paso a paso, con animacion */}
      <DataFlowHero />

      {/* Composición en 2 columnas: Desktop 60% / 40%, Mobile 1 columna */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-12 items-start">
        {/* =========================================================================
            COLUMNA IZQUIERDA: Conversación / Interacción guiada con NADIA (58%)
           ========================================================================= */}
        <div className="space-y-5 lg:col-span-7">
          {/* Card principal del asistente */}
          <div className="rounded-xl border bg-card/60 backdrop-blur-sm p-5 sm:p-6 shadow-sm">
            {/* Header del agente */}
            <div className="flex items-center justify-between gap-3 pb-4 border-b border-border/50">
              <div className="flex items-center gap-3">
                <div className="relative flex size-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-cyan-500 to-indigo-600 text-white shadow-md shadow-cyan-500/20">
                  <Compass className="size-5" />
                  <span className="absolute -bottom-0.5 -right-0.5 size-2.5 rounded-full bg-emerald-400 ring-2 ring-background" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-foreground text-sm">{t('studio.assistantName')}</span>
                    <span className="text-[10px] text-emerald-400 font-mono bg-emerald-500/10 px-1.5 py-0.5 rounded">
                      {t('studio.online')}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {t('studio.assistantRole')}
                  </p>
                </div>
              </div>

              {stage !== 'idle' ? (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleReset}
                  className="h-8 px-3 text-xs font-bold gap-1.5 border-border bg-background/80 hover:bg-primary/10 hover:text-primary cursor-pointer shadow-xs transition-all"
                  title={t('studio.interpreter.startOverBtn', 'Reiniciar proceso')}
                >
                  <RefreshCw className="size-3.5" />
                  <span>{t('studio.interpreter.startOverBtn', 'Reiniciar proceso')}</span>
                </Button>
              ) : null}
            </div>

            {/* Estado Inicial */}
            {stage === 'idle' ? (
              <div className="space-y-5 pt-5">
                <div className="space-y-2">
                  <h2 className="text-lg sm:text-xl font-bold tracking-tight text-foreground">
                    {t('studio.welcomeQuestion')}
                  </h2>
                  <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                    {t('studio.welcomeSub')}
                  </p>
                </div>

                {/* Vías de entrada / Enfoques para la PYME */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <p className="text-xs font-bold tracking-wider uppercase text-foreground/80 flex items-center gap-1.5">
                      <Sparkles className="size-3.5 text-primary" />
                      <span>{t('studio.interpreter.pathwaysHeading')}</span>
                    </p>
                    <span className="text-[11px] text-muted-foreground hidden sm:inline">
                      {t('studio.interpreter.clickToStart', 'Haz clic en una opción o escribe abajo')}
                    </span>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {PATHWAY_PRESETS.map((p) => {
                      const isActive = selectedPathway === p.id
                      return (
                        <button
                          key={p.id}
                          type="button"
                          onClick={() => handleSelectPathway(p)}
                          className={cn(
                            'flex items-center gap-3.5 p-3.5 sm:p-4 rounded-xl border text-left transition-all group relative overflow-hidden cursor-pointer',
                            isActive
                              ? 'border-primary bg-primary/10 text-primary ring-2 ring-primary/30 shadow-md shadow-primary/10'
                              : 'border-border/80 bg-card hover:bg-accent/60 hover:border-primary/50 text-foreground shadow-xs hover:shadow-sm',
                          )}
                        >
                          <div
                            className={cn(
                              'flex size-11 shrink-0 items-center justify-center rounded-xl transition-transform group-hover:scale-105',
                              isActive ? 'bg-primary text-primary-foreground' : 'bg-primary/10 text-primary',
                            )}
                          >
                            {renderIcon(p.icon, 'size-5')}
                          </div>
                          <div className="space-y-0.5 min-w-0 flex-1">
                            <div className="font-bold text-sm tracking-tight text-foreground leading-snug">
                              {t(p.labelKey)}
                            </div>
                            <div className="text-xs text-muted-foreground font-normal leading-normal">
                              {t(p.subKey)}
                            </div>
                          </div>
                        </button>
                      )
                    })}
                  </div>
                </div>

                {/* Caja de texto de entrada */}
                <div className="space-y-3">
                  <div className="relative">
                    <textarea
                      value={inputText}
                      onChange={(e) => {
                        setInputText(e.target.value)
                        setSelectedPathway(null)
                      }}
                      placeholder={t('studio.inputPlaceholder')}
                      rows={3}
                      className="w-full resize-none rounded-lg border bg-background/80 px-3.5 py-2.5 text-sm placeholder:text-muted-foreground/60 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary transition-all"
                    />
                  </div>

                  {/* Barra de interpretación inteligente en tiempo real */}
                  {hasLiveDetected ? (
                    <div className="flex flex-wrap items-center gap-1.5 p-2 rounded-lg bg-primary/5 border border-primary/20 text-xs">
                      <span className="text-[11px] font-semibold text-primary flex items-center gap-1">
                        <Sparkles className="size-3 text-cyan-400" />
                        {t('studio.interpreter.detectedTitle')}:
                      </span>
                      {liveInterpreted.product && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-cyan-500/10 border border-cyan-500/30 text-cyan-300 text-[11px] font-medium">
                          🏷️ {t('studio.interpreter.productDetected')}: {liveInterpreted.product.label[locale]}
                        </span>
                      )}
                      {liveInterpreted.market && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-[11px] font-medium">
                          🌍 {t('studio.interpreter.marketDetected')}: {liveInterpreted.market.label[locale]}
                        </span>
                      )}
                      {liveInterpreted.season && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-amber-500/10 border border-amber-500/30 text-amber-300 text-[11px] font-medium">
                          📅 {t('studio.interpreter.seasonDetected')}: {liveInterpreted.season.label[locale]}
                        </span>
                      )}
                      {liveInterpreted.targetAudience && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-indigo-500/10 border border-indigo-500/30 text-indigo-300 text-[11px] font-medium">
                          👥 {t('studio.interpreter.audienceDetected')}: {liveInterpreted.targetAudience.label[locale]}
                        </span>
                      )}
                    </div>
                  ) : null}

                  <div className="flex justify-end pt-1">
                    <Button
                      onClick={() => handleStartAnalysis()}
                      disabled={!inputText.trim()}
                      className="h-10 px-5 gap-2 shadow-md font-bold text-sm bg-primary hover:bg-primary/90 text-primary-foreground cursor-pointer"
                    >
                      <span>{t('studio.analyzeBtn')}</span>
                      <ArrowRight className="size-4" />
                    </Button>
                  </div>
                </div>

                {/* Acciones Rápidas */}
                <div className="space-y-2 pt-2 border-t border-border/40">
                  <p className="text-[11px] font-semibold tracking-wider uppercase text-muted-foreground">
                    {t('studio.quickPromptTitle')}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {QUICK_ACTIONS.map((action) => (
                      <button
                        key={action.id}
                        type="button"
                        onClick={() => handleStartAnalysis(pick(action.prompt, locale))}
                        className="inline-flex items-center gap-2 rounded-lg border border-border/70 bg-muted/40 hover:bg-muted px-3 py-2 text-xs font-medium text-foreground transition-all hover:border-primary/50 text-left group"
                      >
                        <span className="text-primary group-hover:scale-110 transition-transform">
                          {renderIcon(action.icon, 'size-3.5')}
                        </span>
                        <span>{pick(action.label, locale)}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            ) : null}

            {/* Conversación activa */}
            {stage !== 'idle' ? (
              <div className="space-y-5 pt-5">
                {/* Mensaje de usuario */}
                <div className="flex justify-end">
                  <div className="max-w-[85%] rounded-2xl rounded-tr-sm bg-primary px-4 py-2.5 text-sm text-primary-foreground shadow-sm">
                    <p>{inputText}</p>
                  </div>
                </div>

                {/* 8. Estado de Análisis visual */}
                {stage === 'analyzing' ? (
                  <div className="space-y-3 rounded-xl border border-cyan-500/20 bg-cyan-950/20 p-4">
                    <div className="flex items-center gap-2 text-xs font-semibold text-cyan-400">
                      <Sparkles className="size-3.5 animate-spin" />
                      <span>{t('studio.analyzingTitle')}</span>
                    </div>

                    <div className="space-y-1.5 pt-1">
                      {analysisSteps.map((step, idx) => {
                        const isDone = idx < activeStepIndex
                        const isCurrent = idx === activeStepIndex
                        return (
                          <div
                            key={step.label}
                            className={cn(
                              'flex items-center gap-2.5 text-xs transition-colors',
                              isDone
                                ? 'text-emerald-400 font-medium'
                                : isCurrent
                                  ? 'text-cyan-300 font-semibold'
                                  : 'text-muted-foreground/60',
                            )}
                          >
                            {isDone ? (
                              <CheckCircle2 className="size-3.5 shrink-0" />
                            ) : isCurrent ? (
                              <div className="size-2 rounded-full bg-cyan-400 animate-ping shrink-0 mx-0.5" />
                            ) : (
                              <div className="size-2 rounded-full bg-muted-foreground/40 shrink-0 mx-0.5" />
                            )}
                            <span>{step.label}</span>
                          </div>
                        )
                      })}
                    </div>
                  </div>
                ) : null}

                {/* Respuesta de NADIA tras análisis */}
                {stage !== 'analyzing' ? (
                  <div className="space-y-4">
                    {/* Mensaje de NADIA */}
                    <div className="space-y-2 rounded-xl rounded-tl-sm bg-muted/60 p-4 text-sm leading-relaxed border border-border/50">
                      <div className="flex items-center gap-2 text-xs font-semibold text-primary">
                        <CheckCircle2 className="size-3.5 text-emerald-400" />
                        <span>{t('studio.analysisDone')}</span>
                      </div>

                      {/* Parámetros de negocio interpretados por NADIA */}
                      {derived.interpreted &&
                      (derived.interpreted.product ||
                        derived.interpreted.market ||
                        derived.interpreted.season ||
                        derived.interpreted.targetAudience) ? (
                        <div className="rounded-lg border border-cyan-500/30 bg-cyan-950/25 p-3 text-xs space-y-1.5 my-2">
                          <div className="font-semibold text-cyan-300 flex items-center gap-1.5">
                            <Sparkles className="size-3.5 text-cyan-400" />
                            <span>{t('studio.interpreter.smeTailoredMsg')}</span>
                          </div>
                          <div className="flex flex-wrap gap-1.5 pt-0.5">
                            {derived.interpreted.product ? (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-background/80 border border-border text-foreground text-[11px]">
                                🏷️ <b>{t('studio.interpreter.productDetected')}:</b> {derived.interpreted.product.label[locale]}
                              </span>
                            ) : null}
                            {derived.interpreted.market ? (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-background/80 border border-border text-foreground text-[11px]">
                                🌍 <b>{t('studio.interpreter.marketDetected')}:</b> {derived.interpreted.market.label[locale]}
                              </span>
                            ) : null}
                            {derived.interpreted.season ? (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-background/80 border border-border text-foreground text-[11px]">
                                📅 <b>{t('studio.interpreter.seasonDetected')}:</b> {derived.interpreted.season.label[locale]}
                              </span>
                            ) : null}
                            {derived.interpreted.targetAudience ? (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-background/80 border border-border text-foreground text-[11px]">
                                👥 <b>{t('studio.interpreter.audienceDetected')}:</b> {derived.interpreted.targetAudience.label[locale]}
                              </span>
                            ) : null}
                          </div>
                        </div>
                      ) : null}

                      <p className="text-foreground/90">
                        {t('studio.nadiaAnalysisResult')}
                      </p>
                      <p className="text-foreground/90">
                        {t('studio.nadiaInterestingMarkets')}
                      </p>
                      <p className="text-muted-foreground text-xs pt-1">
                        {t('studio.nadiaPrioritizePrompt')}
                      </p>

                      {/* Botones de selección de prioridad */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 pt-2">
                        {PRIORITY_OPTIONS.map((opt) => (
                          <button
                            key={opt.id}
                            type="button"
                            onClick={() => handleSelectPriority(opt.id)}
                            className={cn(
                              'flex items-center gap-3 rounded-xl border p-3 text-sm font-semibold transition-all group text-left cursor-pointer min-w-0',
                              selectedPriority === opt.id
                                ? 'border-primary bg-primary text-primary-foreground shadow-md ring-2 ring-primary/30'
                                : 'border-border bg-background/90 hover:bg-accent hover:border-primary/50 text-foreground shadow-xs',
                            )}
                          >
                            <span
                              className={cn(
                                'flex size-8 shrink-0 items-center justify-center rounded-lg transition-transform group-hover:scale-105',
                                selectedPriority === opt.id
                                  ? 'bg-white/20 text-white'
                                  : 'bg-primary/10 text-primary',
                              )}
                            >
                              {renderIcon(opt.icon, 'size-4')}
                            </span>
                            <span className="leading-snug">{pick(opt.label, locale)}</span>
                          </button>
                        ))}
                      </div>

                      {!selectedPriority ? (
                        <div className="pt-2 flex justify-end">
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => handleSelectPriority('low_season')}
                            className="gap-2 text-xs font-semibold hover:bg-primary/10 hover:text-primary border border-border/60 cursor-pointer"
                          >
                            <Sparkles className="size-3.5 text-cyan-400" />
                            <span>{t('studio.interpreter.orContinueDirect', 'O ver recomendación sugerida')} →</span>
                          </Button>
                        </div>
                      ) : null}
                    </div>

                    {/* Mensaje tras seleccionar prioridad */}
                    {selectedPriority ? (
                      <div className="space-y-3 rounded-xl rounded-tl-sm bg-muted/60 p-4 text-sm leading-relaxed border border-border/50">
                        <p className="text-foreground/90">
                          {t('studio.nadiaPriorityChosen')}
                        </p>
                        <p className="text-foreground/90">
                          {t('studio.nadiaFirstOpportunity')}
                        </p>

                        {/* 9. OPORTUNIDAD PRINCIPAL */}
                        <div className="rounded-xl border border-cyan-500/30 bg-gradient-to-br from-card via-card to-cyan-950/20 p-4 sm:p-5 shadow-sm space-y-3.5">
                          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border/50 pb-2.5">
                            <div className="flex items-center gap-3">
                              <div className="relative flex size-9 shrink-0 items-center justify-center rounded-lg border border-border/60 bg-muted/30 overflow-hidden shadow-xs">
                                <svg
                                  className="w-full h-full object-cover"
                                  viewBox="0 0 18 11"
                                  aria-label="Finland"
                                >
                                  <rect width="18" height="11" fill="#fff" />
                                  <rect width="18" height="3" y="4" fill="#003580" />
                                  <rect width="3" height="11" x="5" fill="#003580" />
                                </svg>
                              </div>
                              <div>
                                <span className="text-xs font-mono font-bold tracking-wider text-cyan-400 uppercase">
                                  {t('studio.opportunityBadge')}
                                </span>
                                <h3 className="text-base sm:text-lg font-bold text-foreground">
                                  {pick(opportunity.title, locale)}
                                </h3>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <Badge variant="outline" className="border-emerald-500/40 text-emerald-400 bg-emerald-500/10 text-[10px]">
                                {t('studio.potentialLabel')}: {levelLabel(opportunity.potential)}
                              </Badge>
                              <Badge variant="outline" className="border-cyan-500/40 text-cyan-400 bg-cyan-500/10 text-[10px]">
                                {t('studio.confidenceLabel')}: {levelLabel(opportunity.confidence)}
                              </Badge>
                            </div>
                          </div>

                          <p className="text-sm sm:text-base font-semibold text-foreground/95 leading-relaxed">
                            "{pick(opportunity.recommendation, locale)}"
                          </p>

                          {/* Por que esta recomendacion: motivos en lenguaje claro */}
                          <div className="space-y-1.5 pt-2 border-t border-border/40">
                            <span className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">
                              {t('studio.whyChecklistTitle', '¿Por qué esta recomendación?')}
                            </span>
                            <ul className="space-y-1.5 text-xs sm:text-sm">
                              {derived.whyChecklist.map((item) => (
                                <li key={item.key} className="flex items-start gap-2">
                                  <CheckCircle2 className="size-4 text-emerald-400 mt-0.5 shrink-0" />
                                  <span className="text-foreground/90 flex-1">
                                    <b>{pick(item.label, locale)}</b>: {pick(item.value, locale)}
                                  </span>
                                  <ProvenanceBadge kind={item.kind} label={provenanceLabel[item.kind]} />
                                </li>
                              ))}
                            </ul>
                            <p className="text-[11px] text-muted-foreground/80 italic pt-1">
                              {pick(derived.budgetNote, locale)}
                            </p>
                          </div>

                          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-3 border-t border-border/50">
                            <button
                              type="button"
                              onClick={() => setWhyModalOpen(true)}
                              className="inline-flex items-center gap-1.5 text-xs font-semibold text-cyan-400 hover:text-cyan-300 hover:underline cursor-pointer py-1"
                            >
                              <HelpCircle className="size-4" />
                              <span>{t('studio.seeWhyBtn')}</span>
                            </button>

                            {stage === 'opportunity_ready' ? (
                              <Button
                                size="default"
                                onClick={handleGenerateCampaign}
                                className="h-11 px-6 text-sm font-extrabold bg-gradient-to-r from-primary to-cyan-500 hover:from-primary/90 hover:to-cyan-500/90 text-primary-foreground shadow-lg shadow-cyan-500/20 gap-2 cursor-pointer transition-all hover:scale-[1.02] active:scale-[0.98]"
                              >
                                <Sparkles className="size-4.5 animate-pulse" />
                                <span>{t('studio.createCampaignBtn')}</span>
                                <ArrowRight className="size-4 ml-0.5" />
                              </Button>
                            ) : null}
                          </div>
                        </div>
                      </div>
                    ) : null}

                    {/* Estado de generación de campaña */}
                    {stage === 'generating_campaign' ? (
                      <div className="flex items-center gap-3 rounded-xl border border-primary/30 bg-primary/10 p-4 text-xs font-semibold text-primary">
                        <Sparkles className="size-4 animate-spin text-primary" />
                        <span>{t('studio.generatingCampaignText')}</span>
                      </div>
                    ) : null}

                    {/* Botón rápido para reiniciar o hacer otra consulta sin recargar */}
                    <div className="flex flex-wrap items-center justify-between gap-2 pt-3 border-t border-border/40 text-xs">
                      <span className="text-muted-foreground text-[11px]">
                        {t('studio.interpreter.wantToTryAnother', '¿Quieres probar con otro producto, mercado o fecha?')}
                      </span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleReset}
                        className="h-7 px-2.5 text-xs font-bold text-primary hover:bg-primary/10 cursor-pointer gap-1.5"
                      >
                        <RefreshCw className="size-3" />
                        <span>{t('studio.interpreter.newQueryBtn', 'Nueva consulta')}</span>
                      </Button>
                    </div>
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>
        </div>

        {/* =========================================================================
            COLUMNA DERECHA: "Marketing Plan" o Briefing dinámico de campaña (42%)
           ========================================================================= */}
        <div className="space-y-5 lg:col-span-5 lg:sticky lg:top-20">
          <div className="rounded-xl border bg-card p-5 shadow-sm space-y-4">
            <div className="flex items-center justify-between border-b border-border/60 pb-3">
              <div>
                <span className="text-[11px] font-mono font-bold tracking-wider text-muted-foreground uppercase">
                  {t('studio.briefingTag')}
                </span>
                <h2 className="text-base font-bold text-foreground">{t('studio.briefingTitle')}</h2>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="size-2 rounded-full bg-cyan-400 animate-pulse" />
                <span className="text-xs font-medium text-cyan-400">
                  {stage === 'campaign_ready' ? `● ${t('studio.completed')}` : `● ${t('studio.inConstruction')}`}
                </span>
              </div>
            </div>

            {/* Procedencia de los datos: que es real y que es simulado */}
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">
                {t('studio.provenanceTitle', 'Origen de los datos')}:
              </span>
              <ProvenanceBadge kind={derived.provenance.audience} label={`${t('studio.audienceLabel')}: ${provenanceLabel[derived.provenance.audience]}`} />
              <ProvenanceBadge kind={derived.provenance.timing} label={`${t('studio.timingLabel')}: ${provenanceLabel[derived.provenance.timing]}`} />
              <ProvenanceBadge kind={derived.provenance.product} label={`${t('studio.productLabel')}: ${provenanceLabel[derived.provenance.product]}`} />
              <ProvenanceBadge kind={derived.provenance.copy} label={`${t('studio.creativeConceptLabel')}: ${provenanceLabel[derived.provenance.copy]}`} />
            </div>

            {/* Tarjetas del Briefing dinámico */}
            <div className="space-y-3">
              {/* OBJETIVO */}
              <div className="rounded-lg border border-border/60 bg-muted/20 p-3 space-y-1">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1.5 text-xs font-bold text-foreground">
                    <Target className="size-3.5 text-cyan-400" />
                    <span>{t('studio.objectiveLabel')}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    {briefing.objective.confidence ? (
                      <span className="text-[10px] font-mono text-emerald-400">
                        {t('studio.confidenceLabel')}: {levelLabel(briefing.objective.confidence)}
                      </span>
                    ) : null}
                    <Badge
                      variant={briefing.objective.status === 'definido' ? 'default' : 'secondary'}
                      className="text-[10px] py-0 px-1.5 uppercase font-mono"
                    >
                      {t(`studio.${briefing.objective.status}Status`)}
                    </Badge>
                  </div>
                </div>
                <p className="text-sm font-semibold text-foreground/90">{pick(briefing.objective.label, locale)}</p>
                {briefing.objective.detail ? (
                  <p className="text-xs text-muted-foreground">{pick(briefing.objective.detail, locale)}</p>
                ) : null}
              </div>

              {/* AUDIENCIA */}
              <div className="rounded-lg border border-border/60 bg-muted/20 p-3 space-y-1">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1.5 text-xs font-bold text-foreground">
                    <Users className="size-3.5 text-indigo-400" />
                    <span>{t('studio.audienceLabel')}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    {briefing.audience.confidence ? (
                      <span className="text-[10px] font-mono text-emerald-400">
                        {t('studio.confidenceLabel')}: {levelLabel(briefing.audience.confidence)}
                      </span>
                    ) : null}
                    <Badge
                      variant={briefing.audience.status === 'definido' ? 'default' : 'secondary'}
                      className="text-[10px] py-0 px-1.5 uppercase font-mono"
                    >
                      {t(`studio.${briefing.audience.status}Status`)}
                    </Badge>
                  </div>
                </div>
                <p className="text-sm font-semibold text-foreground/90">{pick(briefing.audience.label, locale)}</p>
                {briefing.audience.detail ? (
                  <p className="text-xs text-muted-foreground">{pick(briefing.audience.detail, locale)}</p>
                ) : null}
                {briefing.audience.subdetail ? (
                  <p className="text-xs text-cyan-400/90 font-medium">{pick(briefing.audience.subdetail, locale)}</p>
                ) : null}
              </div>

              {/* MOMENTO */}
              <div className="rounded-lg border border-border/60 bg-muted/20 p-3 space-y-1">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1.5 text-xs font-bold text-foreground">
                    <Calendar className="size-3.5 text-sky-400" />
                    <span>{t('studio.timingLabel')}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    {briefing.timing.confidence ? (
                      <span className="text-[10px] font-mono text-emerald-400">
                        {t('studio.confidenceLabel')}: {levelLabel(briefing.timing.confidence)}
                      </span>
                    ) : null}
                    <Badge
                      variant={briefing.timing.status === 'definido' ? 'default' : 'secondary'}
                      className="text-[10px] py-0 px-1.5 uppercase font-mono"
                    >
                      {t(`studio.${briefing.timing.status}Status`)}
                    </Badge>
                  </div>
                </div>
                <p className="text-sm font-semibold text-foreground/90">{pick(briefing.timing.label, locale)}</p>
                {briefing.timing.detail ? (
                  <p className="text-xs text-muted-foreground">{pick(briefing.timing.detail, locale)}</p>
                ) : null}
              </div>

              {/* PRODUCTO */}
              <div className="rounded-lg border border-border/60 bg-muted/20 p-3 space-y-1">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1.5 text-xs font-bold text-foreground">
                    <Sparkles className="size-3.5 text-amber-400" />
                    <span>{t('studio.productLabel')}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    {briefing.product.confidence ? (
                      <span className="text-[10px] font-mono text-amber-400">
                        {t('studio.confidenceLabel')}: {levelLabel(briefing.product.confidence)}
                      </span>
                    ) : null}
                    <Badge
                      variant={briefing.product.status === 'definido' ? 'default' : 'secondary'}
                      className="text-[10px] py-0 px-1.5 uppercase font-mono"
                    >
                      {t(`studio.${briefing.product.status}Status`)}
                    </Badge>
                  </div>
                </div>
                <p className="text-sm font-semibold text-foreground/90">{pick(briefing.product.label, locale)}</p>
                {briefing.product.detail ? (
                  <p className="text-xs text-muted-foreground">{pick(briefing.product.detail, locale)}</p>
                ) : null}
              </div>

              {/* CANALES */}
              <div className="rounded-lg border border-border/60 bg-muted/20 p-3 space-y-1">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1.5 text-xs font-bold text-foreground">
                    <Radio className="size-3.5 text-emerald-400" />
                    <span>{t('studio.channelsLabel')}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    {briefing.channels.confidence ? (
                      <span className="text-[10px] font-mono text-emerald-400">
                        {t('studio.confidenceLabel')}: {levelLabel(briefing.channels.confidence)}
                      </span>
                    ) : null}
                    <Badge
                      variant={briefing.channels.status === 'definido' ? 'default' : 'secondary'}
                      className="text-[10px] py-0 px-1.5 uppercase font-mono"
                    >
                      {t(`studio.${briefing.channels.status}Status`)}
                    </Badge>
                  </div>
                </div>
                <p className="text-sm font-semibold text-foreground/90">{pick(briefing.channels.label, locale)}</p>
                {briefing.channels.detail ? (
                  <p className="text-xs text-muted-foreground">{pick(briefing.channels.detail, locale)}</p>
                ) : null}
              </div>
            </div>

            {/* CTA Directo para generar la campaña si aún no está lista */}
            {stage !== 'campaign_ready' && stage !== 'generating_campaign' ? (
              <div className="pt-1">
                <Button
                  onClick={() => {
                    if (stage === 'idle') {
                      handleStartAnalysis(inputText || pick(QUICK_ACTIONS[0].prompt, locale))
                    } else {
                      handleGenerateCampaign()
                    }
                  }}
                  className="w-full h-11 text-xs sm:text-sm font-extrabold gap-2 bg-gradient-to-r from-primary to-cyan-500 hover:from-primary/90 hover:to-cyan-500/90 text-primary-foreground shadow-md shadow-primary/20 cursor-pointer transition-all hover:scale-[1.01]"
                >
                  <Sparkles className="size-4 animate-pulse" />
                  <span>
                    {stage === 'idle'
                      ? t('studio.analyzeBtn', 'Analizar y Generar Plan')
                      : t('studio.createCampaignBtn', 'Crear propuesta de campaña')}
                  </span>
                  <ArrowRight className="size-4" />
                </Button>
              </div>
            ) : null}

            {/* Si la campaña ya está lista, permitir crear nuevo escenario desde el Marketing Plan */}
            {stage === 'campaign_ready' ? (
              <div className="pt-1">
                <Button
                  variant="outline"
                  onClick={handleReset}
                  className="w-full h-9 text-xs font-bold gap-1.5 border-border hover:bg-primary/10 hover:text-primary cursor-pointer shadow-xs transition-all"
                >
                  <RefreshCw className="size-3.5" />
                  <span>{t('studio.interpreter.startOverBtn', 'Reiniciar proceso')}</span>
                </Button>
              </div>
            ) : null}

            {/* Aviso de procedencia + fuentes usadas */}
            <div className="pt-2 space-y-1 text-center">
              <span className="text-[10px] text-muted-foreground/80">
                {t('studio.briefingDisclaimer', 'Mercado y ventana: datos medidos (Statistics Finland, Google Analytics). La puntuación de producto y el copy son simulados y se marcan como tales.')}
              </span>
              {derived.sources.length ? (
                <p className="text-[10px] text-muted-foreground/60">
                  {t('studio.sourcesUsed', 'Fuentes usadas')}: {derived.sources.join(' · ')}
                </p>
              ) : null}
            </div>
          </div>
        </div>
      </div>

      {/* =========================================================================
          SECCIÓN INFERIOR: CAMPAÑA PROPUESTA (Generada tras pulsar el CTA)
         ========================================================================= */}
      {stage === 'campaign_ready' ? (
        <section
          aria-label={t('studio.campaignProposedTitle')}
          className="rounded-2xl border border-primary/30 bg-card p-6 sm:p-8 shadow-lg space-y-8 animate-in fade-in duration-500"
        >
          {/* Cabecera de la campaña lista */}
          <div className="flex flex-wrap items-center justify-between gap-4 border-b border-border/60 pb-5">
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <span className="inline-flex size-6 items-center justify-center rounded-full bg-emerald-500/20 text-emerald-400">
                  <Check className="size-3.5" />
                </span>
                <span className="font-mono text-xs font-bold tracking-wider text-emerald-400 uppercase">
                  {t('studio.campaignReadyBadge')}
                </span>
              </div>
              <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight">
                {t('studio.campaignProposedTitle')}: {campaign.title}
              </h2>
              <p className="text-xs sm:text-sm text-muted-foreground">
                {pick(campaign.subtitle, locale)}
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleCopyBriefing}
                className="text-xs gap-1.5"
              >
                <Copy className="size-3.5" />
                <span>{copiedNotification ? t('studio.briefingCopied') : t('studio.exportBriefingBtn')}</span>
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleReset}
                className="text-xs gap-1.5"
              >
                <RefreshCw className="size-3.5" />
                <span>{t('studio.createOtherScenarioBtn')}</span>
              </Button>
              <Link to="/sources">
                <Button variant="ghost" size="sm" className="text-xs gap-1.5">
                  <BookOpen className="size-3.5" />
                  <span>{t('studio.viewSourcesBtn')}</span>
                </Button>
              </Link>
            </div>
          </div>

          {/* Bloques de Estrategia */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="rounded-xl border bg-muted/20 p-4 space-y-1">
              <div className="flex items-center gap-1.5 text-muted-foreground">
                <Target className="size-3.5 text-cyan-400" />
                <span className="text-[11px] font-mono font-bold uppercase">{t('studio.objectiveLabel')}</span>
              </div>
              <p className="text-sm font-semibold text-foreground">
                {pick(campaign.objective, locale)}
              </p>
            </div>
            <div className="rounded-xl border bg-muted/20 p-4 space-y-1">
              <div className="flex items-center gap-1.5 text-muted-foreground">
                <Users className="size-3.5 text-indigo-400" />
                <span className="text-[11px] font-mono font-bold uppercase">{t('studio.audienceLabel')}</span>
              </div>
              <p className="text-sm font-semibold text-foreground">
                {pick(campaign.audience, locale)}
              </p>
            </div>
            <div className="rounded-xl border bg-muted/20 p-4 space-y-1">
              <div className="flex items-center gap-1.5 text-muted-foreground">
                <Calendar className="size-3.5 text-sky-400" />
                <span className="text-[11px] font-mono font-bold uppercase">{t('studio.timingLabel')}</span>
              </div>
              <p className="text-sm font-semibold text-foreground">
                {campaign.period}
              </p>
            </div>
          </div>

          {/* Concepto Creativo */}
          <div className="rounded-xl border border-cyan-500/30 bg-gradient-to-r from-card via-cyan-950/20 to-card p-5 space-y-3">
            <div className="flex items-center gap-2">
              <Lightbulb className="size-4 text-cyan-400" />
              <span className="text-xs font-mono font-bold tracking-wider uppercase text-cyan-400">
                {t('studio.creativeConceptLabel')}
              </span>
            </div>
            <p className="text-xl sm:text-2xl font-bold tracking-tight text-foreground italic">
              "{campaign.creativeConcept}"
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-1">
              <div>
                <span className="text-[11px] font-semibold text-muted-foreground uppercase">
                  {t('studio.mainMessageLabel')}
                </span>
                <p className="text-sm text-foreground/90 font-medium">
                  {campaign.mainMessage}
                </p>
              </div>
              <div>
                <span className="text-[11px] font-semibold text-muted-foreground uppercase">
                  {t('studio.ctaButtonLabel')}
                </span>
                <p className="text-sm text-primary font-bold">
                  {campaign.cta}
                </p>
              </div>
            </div>
          </div>

          {/* 11. Canales recomendados */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-bold text-foreground">{t('studio.recommendedChannelsTitle')}</h3>
              <span className="text-xs text-muted-foreground">{t('studio.channelsSubtitle')}</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3.5">
              {campaign.channels.map((chan) => (
                <div
                  key={chan.id}
                  className="rounded-xl border bg-muted/20 p-4 space-y-2 flex flex-col justify-between"
                >
                  <div className="space-y-1.5">
                    <div className="flex items-center justify-between">
                      <span className="font-bold text-foreground text-sm">{chan.name}</span>
                      <Badge variant={chan.badgeVariant} className="text-[10px] font-mono">
                        {t('studio.priorityLabel')}: {chan.priority}
                      </Badge>
                    </div>
                    <p className="text-xs text-muted-foreground leading-relaxed">
                      {pick(chan.explanation, locale)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* 12. Contenidos recomendados y Preview interactiva */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-bold text-foreground">{t('studio.recommendedContentsTitle')}</h3>
              <span className="text-xs text-muted-foreground">{t('studio.contentsSubtitle')}</span>
            </div>

            <div className="flex flex-wrap gap-2">
              {campaign.contents.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => setActiveContentId(item.id)}
                  className={cn(
                    'rounded-lg border px-3.5 py-2 text-xs font-semibold transition-all',
                    activeContentId === item.id
                      ? 'border-primary bg-primary text-primary-foreground shadow-sm'
                      : 'border-border bg-muted/30 hover:bg-muted text-foreground',
                  )}
                >
                  [{item.name}]
                </button>
              ))}
            </div>

            {/* Preview del contenido activo */}
            <div className="rounded-xl border border-border bg-muted/10 p-5 space-y-4">
              <div className="flex items-center justify-between border-b border-border/50 pb-2.5">
                <div className="flex items-center gap-2">
                  <span className="font-mono text-xs font-bold text-primary">
                    {t('studio.previewLabel')}: {activeContent.name}
                  </span>
                  <Badge variant="outline" className="text-[10px]">
                    {t('studio.formatLabel')} {activeContent.type}
                  </Badge>
                </div>
                <span className="text-xs text-muted-foreground">{t('studio.sampleCreativeBadge')}</span>
              </div>

              <div className="space-y-2.5 max-w-xl">
                <div>
                  <span className="text-[11px] font-mono uppercase text-muted-foreground">
                    {t('studio.headlineLabel')}
                  </span>
                  <p className="text-base font-bold text-foreground">{activeContent.headline}</p>
                </div>

                <div>
                  <span className="text-[11px] font-mono uppercase text-muted-foreground">
                    {t('studio.bodyLabel')}
                  </span>
                  <p className="text-sm text-foreground/80 leading-relaxed">{activeContent.body}</p>
                </div>

                <div className="pt-1">
                  <span className="text-[11px] font-mono uppercase text-muted-foreground">
                    {t('studio.ctaLabel')}
                  </span>
                  <div className="mt-1">
                    <Button size="sm" className="text-xs font-bold shadow-sm">
                      {activeContent.cta}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      ) : null}

      {/* 7. Modal "¿Por qué recomienda esto?" */}
      {whyModalOpen ? (
        <div
          role="dialog"
          aria-modal="true"
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm animate-in fade-in duration-200"
        >
          <div className="relative w-full max-w-lg rounded-2xl border bg-card p-6 shadow-2xl space-y-5">
            <button
              type="button"
              onClick={() => setWhyModalOpen(false)}
              aria-label="Cerrar modal"
              className="absolute right-4 top-4 rounded-md p-1.5 text-muted-foreground hover:bg-accent hover:text-foreground"
            >
              <X className="size-4" />
            </button>

            <div className="space-y-1">
              <Badge variant="outline" className="text-cyan-400 border-cyan-500/30 bg-cyan-500/10 text-[10px]">
                {pick(opportunity.why.subtitle, locale)}
              </Badge>
              <h3 className="text-xl font-bold text-foreground">
                {pick(opportunity.why.title, locale)}
              </h3>
            </div>

            {/* Puntos clave */}
            <div className="space-y-2">
              <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                {t('studio.whyModalCriterias')}
              </span>
              <ul className="space-y-2 text-sm text-foreground/90">
                {(opportunity.why.points[locale] ?? opportunity.why.points.en).map((pt, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <CheckCircle2 className="size-4 text-emerald-400 mt-0.5 shrink-0" />
                    <span>{pt}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Fuentes verificadas */}
            <div className="space-y-2 rounded-xl border bg-muted/20 p-3.5">
              <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                {t('studio.whyModalSources')}
              </span>
              <ul className="space-y-1 text-xs text-foreground/80">
                {opportunity.why.sources.map((src, i) => (
                  <li key={i} className="flex items-center gap-1.5">
                    <span className="size-1.5 rounded-full bg-cyan-400" />
                    <span>{src}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="flex items-center justify-between border-t border-border/50 pt-3 text-xs">
              <div className="flex items-center gap-1.5">
                <span className="text-muted-foreground">{t('studio.whyConfidence')}</span>
                <Badge variant="success" className="font-mono text-[10px]">
                  {levelLabel(opportunity.why.confidence)}
                </Badge>
              </div>
              <span className="text-[10px] text-muted-foreground/80 italic">
                {pick(opportunity.why.disclaimer, locale)}
              </span>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  )
}
